import { Link } from '@tanstack/react-router';
import { RotateCw, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  BACKGROUND_JOB_STATE_LABELS,
  BACKGROUND_QUEUE_INFO,
  FAILURE_CAUSE_INFO,
  failureCauseOf,
  jobActionRefusalText,
  type BackgroundJobDetail,
  type BackgroundJobRow,
  type BackgroundQueue,
} from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { SkeletonLines } from '../../../../components/Skeleton';
import { ServerErrorState } from '../../../../components/system-states/SystemState';
import { ToneChip } from '../../../../components/Tone';
import { errorMessage } from '../../../../lib/api-client';
import { EMPTY, formatDateTime } from '../../../../lib/format';
import { useToasts } from '../../../../stores/toasts';
import { useBackgroundJob, useRemoveJob, useRetryJob } from '../../api/background-work-api';
import { ConfirmBeside } from '../ConfirmBeside';
import { PaneFrame, PaneSection, type PaneMode } from '../people/PaneFrame';
import { JOB_STATE_TONES } from './background-work-address';
import styles from './JobPane.module.css';

/**
 * Background work's pane (Option A): why the job stopped in plain words, its details, Retry, and Remove with a confirm
 * beside the button (a removed job can't be put back). An email job is judged by Email delivery's rules: Retry is a
 * Send again on its message, refused for an email that held a sign-in link or is already on its way.
 */
export function JobPane({
  queue,
  id,
  row,
  mode,
  canEdit,
  onClose,
}: {
  readonly queue: BackgroundQueue;
  readonly id: string;
  readonly row: BackgroundJobRow | undefined;
  readonly mode: PaneMode;
  readonly canEdit: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const detail = useBackgroundJob(queue, id, true);
  const [isRawOpen, setRawOpen] = useState(false);
  const shown: BackgroundJobRow | undefined = detail.data ?? row;

  if (!shown) {
    return (
      <PaneFrame mode={mode} label="Job" title={detail.isError ? 'This job is no longer there' : 'Loading…'} onClose={onClose}>
        <PaneSection label="Job">
          {detail.isError ? (detail.error.status === 404 ? <p className={styles.quiet}>{detail.error.message}</p> : <ServerErrorState requestId={detail.error.requestId} onRetry={() => void detail.refetch()} />) : <SkeletonLines count={5} />}
        </PaneSection>
      </PaneFrame>
    );
  }

  const title = shown.message ? `${shown.label} — to ${shown.message.toAddress}` : shown.label;
  return (
    <PaneFrame mode={mode} label={`Job: ${shown.label}`} title={title} sub={`${shown.name} #${shown.id}`} leading={<ToneChip tone={JOB_STATE_TONES[shown.state]}>{BACKGROUND_JOB_STATE_LABELS[shown.state]}</ToneChip>} onClose={onClose}>
      {shown.state === 'failed' && (
        <PaneSection title="Why it stopped">
          <WhyItStopped row={shown} />
        </PaneSection>
      )}

      {detail.data && canEdit && shown.state === 'failed' && <Actions queue={queue} detail={detail.data} onDone={onClose} />}
      {!canEdit && shown.state === 'failed' && (
        <PaneSection label="Read only">
          <p className={styles.quiet}>You can look at this job, but not retry or remove it.</p>
        </PaneSection>
      )}

      <PaneSection title="Details">
        <dl className={styles.details}>
          <dt>Queue</dt>
          <dd>
            {BACKGROUND_QUEUE_INFO[queue].label} <span className={styles.mono}>{queue}</span>
          </dd>
          <dt>Added</dt>
          <dd className={styles.number}>{shown.addedAt ? formatDateTime(shown.addedAt) : EMPTY}</dd>
          {shown.runsAt && (
            <>
              <dt>Runs at</dt>
              <dd className={styles.number}>{formatDateTime(shown.runsAt)}</dd>
            </>
          )}
          <dt>Last try</dt>
          <dd className={styles.number}>{shown.finishedAt ? formatDateTime(shown.finishedAt) : shown.startedAt ? formatDateTime(shown.startedAt) : EMPTY}</dd>
          <dt>Tries</dt>
          <dd className={styles.number}>
            {shown.attemptsMade} of {shown.maxAttempts}
          </dd>
          <dt>Started by</dt>
          <dd>{shown.isScheduled ? 'Scheduled work' : 'Something someone did'}</dd>
          {shown.message && (
            <>
              <dt>For</dt>
              <dd>
                <Link to="/admin/email" search={{ open: shown.message.messageId }} className={styles.link}>
                  {shown.message.subject}
                </Link>
              </dd>
            </>
          )}
        </dl>
      </PaneSection>

      {detail.data && (
        <PaneSection label="Raw data">
          <Button variant="ghost" size="sm" aria-expanded={isRawOpen} onClick={() => setRawOpen(!isRawOpen)}>
            {isRawOpen ? 'Hide raw data' : 'Show raw data'}
          </Button>
          {isRawOpen && <pre className={styles.raw}>{JSON.stringify(detail.data.data, null, 2)}</pre>}
        </PaneSection>
      )}
    </PaneFrame>
  );
}

/** The server's words, in a sentence for an email (its SMTP cause), with the raw reason kept beside it. */
function WhyItStopped({ row }: { readonly row: BackgroundJobRow }): ReactNode {
  const cause = row.message ? FAILURE_CAUSE_INFO[failureCauseOf(row.failedReason)] : null;
  return (
    <>
      <p className={styles.reason}>
        {cause ? `${cause.label}. ` : ''}It tried {row.attemptsMade === 1 ? 'once' : `${row.attemptsMade} times`}, then stopped.
        {cause ? ` ${cause.advice}` : ''}
      </p>
      {row.failedReason && <p className={styles.mono}>{row.failedReason}</p>}
      {row.message && (
        <p className={styles.quiet}>
          Failed emails also appear on{' '}
          <Link to="/admin/email" search={{ open: row.message.messageId }} className={styles.link}>
            Email delivery
          </Link>
          , where an address can be fixed before sending again. Retrying here sends it exactly as it was.
        </p>
      )}
    </>
  );
}

function Actions({ queue, detail, onDone }: { readonly queue: BackgroundQueue; readonly detail: BackgroundJobDetail; readonly onDone: () => void }): ReactNode {
  const retry = useRetryJob();
  const remove = useRemoveJob();
  const showToast = useToasts((state) => state.show);
  const job = { queue, id: detail.id };
  const failed = (message: string) => (error: unknown) => showToast({ tone: 'danger', message, detail: errorMessage(error) });
  const doRetry = (): void => {
    onDone();
    retry.mutate(job, { onSuccess: () => showToast({ tone: 'success', message: detail.message ? 'Sent again' : 'Retrying' }), onError: failed('It was not retried') });
  };

  return (
    <PaneSection label="Actions">
      {detail.retryRefusal !== null && <p className={styles.refusal}>{jobActionRefusalText(detail.retryRefusal, detail.message?.templateKey ?? null)}</p>}
      <div className={styles.buttons}>
        {detail.retryRefusal === null &&
          (detail.message ? (
            // An email goes to a person: like Send again on Email delivery, it is asked beside its button.
            <ConfirmBeside
              label="Retry"
              icon={RotateCw}
              variant="solid"
              isPending={retry.isPending}
              title="Send this email again?"
              body={
                <>
                  “{detail.message.subject}” goes to <b>{detail.message.toAddress}</b> again, as it was. It can't be taken back once it has gone.
                </>
              }
              confirmLabel="Send again"
              onConfirm={doRetry}
            />
          ) : (
            <Button variant="solid" size="sm" icon={RotateCw} isPending={retry.isPending} onClick={doRetry}>
              Retry
            </Button>
          ))}
        {detail.removeRefusal === null && (
          <ConfirmBeside
            label="Remove"
            icon={Trash2}
            variant="ghost"
            isDanger
            isPending={remove.isPending}
            title="Remove this job?"
            body={detail.message ? 'The job goes from this list and cannot be put back. The email stays failed on Email delivery, with its history.' : 'The job goes from this list and cannot be put back. Whatever it was doing will not happen.'}
            confirmLabel="Remove"
            onConfirm={() => {
              onDone();
              remove.mutate(job, { onSuccess: () => showToast({ tone: 'success', message: 'Job removed' }), onError: failed('It was not removed') });
            }}
          />
        )}
      </div>
    </PaneSection>
  );
}
